import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, Linkedin, Download, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';

const BrandonPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Brandon Haley - CEO | CTC Waterworks"
        description="Connect with Brandon Haley, CEO of CTC Waterworks. Get his contact information and learn about CTC's agricultural water solutions."
        canonicalPath="/team/brandon"
      />
      <Navbar />

      {/* Header Section */}
      <section className="pt-24 pb-8 bg-gradient-to-br from-slate-50 via-ctc-silver-light to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link to="/" className="inline-block mb-6">
              <img
                src="/CTC_Waterworks Logo_Blue_White.png"
                alt="CTC Waterworks"
                className="h-16 w-auto mx-auto"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling;
                  if (fallback) (fallback as HTMLElement).style.display = 'block';
                }}
              />
              <div style={{ display: 'none' }} className="text-3xl font-bold text-ctc-blue">
                CTC Waterworks
              </div>
            </Link>
            <p className="text-ctc-blue font-semibold text-lg mb-8">Better Water = Better Profits</p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 bg-white">
        <div className="max-w-2xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-12"
          >
            <div className="w-32 h-32 rounded-full overflow-hidden mx-auto mb-6 border-4 border-ctc-blue/20 shadow-xl">
              <img
                src="/Brandon Haley.png"
                alt="Brandon Haley"
                className="w-full h-full object-cover"
              />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
              Brandon Haley
            </h1>
            <p className="text-xl text-gray-600 font-semibold mb-2">CEO - Owner</p>
            <p className="text-sm text-gray-500">Tap below to save my contact directly to your phone</p>
          </motion.div>

          {/* Contact Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-4 mb-12"
          >
            <a
              href="/Brandon-Haley.R12.25.vcf"
              download
              className="w-full bg-gradient-to-r from-ctc-blue to-ctc-blue-dark text-white px-8 py-5 rounded-xl font-semibold hover:from-ctc-blue-dark hover:to-ctc-blue transition-all duration-300 inline-flex items-center justify-center space-x-3 shadow-lg text-lg"
            >
              <Download size={24} />
              <span>Add Brandon to Your Contacts</span>
            </a>

            <a
              href="tel:+15099302185"
              className="w-full bg-white border-2 border-ctc-blue text-ctc-blue px-8 py-5 rounded-xl font-semibold hover:bg-ctc-blue hover:text-white transition-all duration-300 inline-flex items-center justify-center space-x-3 shadow-md text-lg"
            >
              <Phone size={24} />
              <span>Call Brandon</span>
            </a>

            <a
              href="mailto:Brandon@CTCWaterworks.com"
              className="w-full bg-white border-2 border-ctc-blue text-ctc-blue px-8 py-5 rounded-xl font-semibold hover:bg-ctc-blue hover:text-white transition-all duration-300 inline-flex items-center justify-center space-x-3 shadow-md text-lg"
            >
              <Mail size={24} />
              <span>Email Brandon</span>
            </a>

            <a
              href="https://www.linkedin.com/in/brandon-haley-154b0033/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-white border-2 border-ctc-blue text-ctc-blue px-8 py-5 rounded-xl font-semibold hover:bg-ctc-blue hover:text-white transition-all duration-300 inline-flex items-center justify-center space-x-3 shadow-md text-lg"
            >
              <Linkedin size={24} />
              <span>Connect on LinkedIn</span>
            </a>
          </motion.div>

          {/* About Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-ctc-blue/5 to-ctc-blue/10 rounded-2xl p-8 border border-ctc-blue/20 mb-12"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">About Brandon</h2>
            <p className="text-gray-700 leading-relaxed">
              Brandon leads CTC Waterworks, helping growers improve irrigation water quality with sulfur
              burners, hydrocoolers, and filtration systems so crops and pack-outs perform better. With
              decades of hands-on experience in agricultural manufacturing and a commitment to building
              reliable systems, Brandon ensures every grower gets a solution that works.
            </p>
          </motion.div>

          {/* CTC Links Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border border-gray-200"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Explore CTC Solutions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/sulfur-burners"
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg border border-gray-200 hover:border-ctc-blue transition-all duration-300 group"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-ctc-blue transition-colors">
                  Sulfur Burners
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Improve water quality and soil pH management
                </p>
                <div className="flex items-center text-ctc-blue text-sm font-medium">
                  <span>Learn More</span>
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
                </div>
              </Link>

              <Link
                to="/hydrocoolers"
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg border border-gray-200 hover:border-ctc-blue transition-all duration-300 group"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-ctc-blue transition-colors">
                  Hydrocoolers
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Preserve freshness and extend shelf life
                </p>
                <div className="flex items-center text-ctc-blue text-sm font-medium">
                  <span>Learn More</span>
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
                </div>
              </Link>

              <Link
                to="/filtration"
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg border border-gray-200 hover:border-ctc-blue transition-all duration-300 group"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-ctc-blue transition-colors">
                  Filtration Systems
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Protect irrigation systems and equipment
                </p>
                <div className="flex items-center text-ctc-blue text-sm font-medium">
                  <span>Learn More</span>
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
                </div>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BrandonPage;
